import {
  ColorMeasurementMethod,
  ColorProcessErrorV2,
  ColorProcessMethodV2,
  ColorProcessOpticalProfileV2,
  ColorProcessRequestV2,
  ColorProcessResponseV2,
  ColorProcessSuccessV2
} from "../types";

export const COLOR_PROCESS_CONTRACT_VERSION = "color-process.v2";
export const COLOR_PROCESS_CALCULATION_VERSION = "color_measurement.v2";

type ColorProcessRequestBuildParams = {
  inputPath: string;
  relativePath?: string;
  measurementName?: string;
  measurementLabel?: string;
  thicknessCsvPath?: string;
  inputFe2O3Percent?: number;
  inputFeTotalPercent?: number;
  method: ColorMeasurementMethod;
  opticalProfile?: ColorProcessOpticalProfileV2 | null;
  calculationVersion?: string;
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === "object" && !Array.isArray(value);
}

function assertNonEmptyString(value: unknown, fieldName: string) {
  if (typeof value !== "string" || !value.trim()) {
    throw new Error(`Color processing contract error: ${fieldName} must be a non-empty string.`);
  }
  return value.trim();
}

function assertFiniteNumber(value: unknown, fieldName: string) {
  if (!Number.isFinite(value)) {
    throw new Error(`Color processing contract error: ${fieldName} must be numeric.`);
  }
  return Number(value);
}

export function buildColorProcessMethodRequest(
  method: ColorMeasurementMethod
): ColorProcessMethodV2 {
  if (!method.observer_code) {
    throw new Error(`Color method "${method.name}" is missing observer_code.`);
  }
  if (!method.illuminant_code) {
    throw new Error(`Color method "${method.name}" is missing illuminant_code.`);
  }
  if (!method.reflection_correction_code) {
    throw new Error(`Color method "${method.name}" is missing reflection_correction_code.`);
  }

  return {
    observer_code: method.observer_code,
    illuminant_code: method.illuminant_code,
    wavelength_min_nm: method.wavelength_min_nm,
    wavelength_max_nm: method.wavelength_max_nm,
    wavelength_step_nm: method.wavelength_step_nm,
    standard_thickness_mm: method.standard_thickness_mm,
    reflection_correction_code: method.reflection_correction_code,
    surface_reflectivity_pct_default: method.surface_reflectivity_pct_default ?? null,
    reference_medium_name: method.reference_medium_name ?? null,
    reference_medium_notes: method.reference_medium_notes ?? null
  };
}

export function buildColorProcessRequest({
  inputPath,
  relativePath,
  measurementName,
  measurementLabel,
  thicknessCsvPath,
  inputFe2O3Percent,
  inputFeTotalPercent,
  method,
  opticalProfile,
  calculationVersion = COLOR_PROCESS_CALCULATION_VERSION
}: ColorProcessRequestBuildParams): ColorProcessRequestV2 {
  const chemistry: ColorProcessRequestV2["chemistry"] = {};
  if (Number.isFinite(inputFe2O3Percent)) {
    chemistry.input_fe2o3_percent = Number(inputFe2O3Percent);
  }
  if (Number.isFinite(inputFeTotalPercent)) {
    chemistry.input_fe_total_percent = Number(inputFeTotalPercent);
  }
  return {
    contract_version: COLOR_PROCESS_CONTRACT_VERSION,
    measurement: {
      input_path: inputPath,
      relative_path: relativePath,
      measurement_name: measurementName,
      measurement_label: measurementLabel,
      thickness_csv_path: thicknessCsvPath
    },
    method: buildColorProcessMethodRequest(method),
    optical_profile: opticalProfile || undefined,
    chemistry,
    options: {
      calculation_version: calculationVersion
    }
  };
}

export function isColorProcessSuccess(
  response: ColorProcessResponseV2
): response is ColorProcessSuccessV2 {
  return response.status === "ok";
}

export function isColorProcessError(
  response: ColorProcessResponseV2
): response is ColorProcessErrorV2 {
  return response.status === "error";
}

export function parseColorProcessResponse(payload: unknown): ColorProcessResponseV2 {
  if (!isRecord(payload)) {
    throw new Error("Color processing contract error: payload must be an object.");
  }

  const contractVersion = assertNonEmptyString(payload.contract_version, "contract_version");
  if (contractVersion !== COLOR_PROCESS_CONTRACT_VERSION) {
    throw new Error(
      `Color processing contract error: expected ${COLOR_PROCESS_CONTRACT_VERSION}, received ${contractVersion}.`
    );
  }

  const status = assertNonEmptyString(payload.status, "status");
  if (status === "ok") {
    if (!isRecord(payload.measurement)) {
      throw new Error("Color processing contract error: success payload is missing measurement.");
    }
    if (!isRecord(payload.curves)) {
      throw new Error("Color processing contract error: success payload is missing curves.");
    }
    if (!Array.isArray(payload.warnings)) {
      throw new Error("Color processing contract error: success payload is missing warnings.");
    }
    if (!Array.isArray(payload.references_applied)) {
      throw new Error(
        "Color processing contract error: success payload is missing references_applied."
      );
    }
    assertNonEmptyString(payload.measurement.measured_at, "measurement.measured_at");
    assertNonEmptyString(payload.measurement.calculation_version, "measurement.calculation_version");
    assertFiniteNumber(payload.measurement.applied_wavelength_min_nm, "measurement.applied_wavelength_min_nm");
    assertFiniteNumber(payload.measurement.applied_wavelength_max_nm, "measurement.applied_wavelength_max_nm");
    assertFiniteNumber(payload.measurement.applied_wavelength_step_nm, "measurement.applied_wavelength_step_nm");
    return payload as ColorProcessSuccessV2;
  }

  if (status === "error") {
    if (!isRecord(payload.error)) {
      throw new Error("Color processing contract error: error payload is missing error object.");
    }
    assertNonEmptyString(payload.error.code, "error.code");
    assertNonEmptyString(payload.error.message, "error.message");
    if (typeof payload.error.retryable !== "boolean") {
      throw new Error("Color processing contract error: error.retryable must be boolean.");
    }
    return payload as ColorProcessErrorV2;
  }

  throw new Error(`Color processing contract error: unsupported status "${status}".`);
}
